/**
 * Resolves the "next step" call-to-action for a Track's Item Pool section
 * (BREAK 1 — "Lock pool ✕ Rank in session").
 *
 * Canon (`docs/1-canon/01-terminology-and-glossary.md`, "Item Pool"):
 * "Membership must be locked before a force-ranking Session begins." Locking
 * a pool used to be a dead end — nothing told the user that ranking the
 * locked pool in a Session is the next step, or took them there. This is the
 * pure decision behind that CTA: pending while the pool is open, "start" once
 * it locks and no ranking Session exists for the Track yet, "open" once one
 * does. Kept pure and separate from `startTrackRankingSession.ts` (the side
 * effect that acts on `'start'`) so the branch logic is unit-testable without
 * mounting a store or a component.
 */
import type { PrioritizationSession } from '@/types/prioritization/session';

export type TrackPoolRankingStep =
  /** Pool isn't locked yet (or there's no track) — no CTA to show. */
  | { kind: 'pending' }
  /** Pool is locked and no ranking Session exists for this track yet. */
  | { kind: 'start' }
  /** Pool is locked and a ranking Session already exists — link to it. */
  | { kind: 'open'; sessionId: string };

/** Minimal session shape the resolver needs — real sessions satisfy this. */
export type RankingSessionLookup = Pick<PrioritizationSession, 'id' | 'trackId' | 'intent'>;

/**
 * The Session that ranks a track's pool is the one with intent
 * `'rank_priorities'` linked to that track — matches how
 * `sessionStore.createSession` resolves a rank_priorities session's items
 * from `itemPoolsStore`'s track-scoped pool (`intent === 'rank_priorities'
 * && params.trackId` branch), so "does a ranking session exist" and "will
 * creating one use this pool" agree by construction.
 */
export function findTrackRankingSession(
  sessions: RankingSessionLookup[],
  trackId: string,
): RankingSessionLookup | undefined {
  // Defensive, matching this codebase's own idiom at the hook boundary
  // (`Array.isArray(rawSessions) ? rawSessions : []` in TrackDetail.tsx):
  // a zustand-backed sessions array observed mid-render (e.g. right after a
  // tab switch, before the store's snapshot settles) can transiently reach
  // here as something other than an array — never throw the whole pool tab
  // over a value that will be a real array on the very next render.
  if (!Array.isArray(sessions)) return undefined;
  return sessions.find((s) => s.trackId === trackId && s.intent === 'rank_priorities');
}

export function resolveTrackPoolRankingStep(params: {
  poolLocked: boolean;
  trackId: string | null | undefined;
  sessions: RankingSessionLookup[];
}): TrackPoolRankingStep {
  if (!params.poolLocked || !params.trackId) return { kind: 'pending' };
  const existing = findTrackRankingSession(params.sessions, params.trackId);
  return existing ? { kind: 'open', sessionId: existing.id } : { kind: 'start' };
}
